import React from "react";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useContext, useEffect, useRef, useState } from "react";
import AppContext from "../../context/AppContext";
import { toast } from "../../utils/toast";
import { createOrganization } from "../../service/User";
import { uploadFileToS3 } from "../../service/S3Bucket";
import { Trash2 } from "lucide-react";
import { TbLoader } from "react-icons/tb";
import translations from "../common/Translations";
import CommonButton from "../common/CommonButton";
import ModalCloseButton from "../common/ModalCloseButton";

export default function CreateOrganization({
  open,
  setOpen,
  fetchorgnizationlist,
}) {
  const [isLoading, setIsLoading] = useState(false); // State to manage loading
  const [selectedOrgName, setSelectedOrgName] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [logoPreview, setLogoPreview] = useState(null);
  const [isLogoUploading, setIsLogoUploading] = useState(false);
  const [reportEmails, setReportEmails] = useState([""]);
  const cancelButtonRef = useRef(null);
  const fileInputRef = useRef(null);
  const { theme, langMode } = useContext(AppContext);
  const isRtl = langMode === "ar" || langMode === "ur";

  useEffect(() => {
    if (!open) {
      setLogoUrl("");
      setLogoPreview(null);
      setIsLogoUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  }, [open]);

  const handleOrgNameChange = (event) => {
    setSelectedOrgName(event.target.value);
  };
  const handleLogoChange = async (event) => {
    const file = event.target.files?.[0];
    if (fileInputRef.current) fileInputRef.current.value = "";
    if (!file) {
      setLogoUrl("");
      setLogoPreview(null);
      return;
    }
    if (!file.type.startsWith("image/")) {
      toast.error(translations[langMode].invalidfiletypeplimg || "Please select a valid image file.");
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => {
      setLogoPreview(reader.result);
    };
    reader.readAsDataURL(file);

    setIsLogoUploading(true);
    setLogoUrl("");
    try {
      const uploadedUrl = await uploadFileToS3(file);
      if (!uploadedUrl) {
        throw new Error("Upload failed");
      }
      setLogoUrl(uploadedUrl);
    } catch {
      setLogoPreview(null);
      setLogoUrl("");
      toast.error(translations[langMode].erroruploadingfileplztry || "Error uploading image. Please try again.");
    } finally {
      setIsLogoUploading(false);
    }
  };
  const addReportEmail = () => setReportEmails((prev) => [...prev, ""]);
  const removeReportEmail = (index) => {
    if (reportEmails.length <= 1) return;
    setReportEmails((prev) => prev.filter((_, i) => i !== index));
  };
  const setReportEmailAt = (index, value) => {
    setReportEmails((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const isValidEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());

  const handleAddOrganization = async () => {
    const emails = reportEmails.map((e) => e.trim()).filter(Boolean);
    if (emails.length > 0) {
      const invalid = emails.filter((e) => !isValidEmail(e));
      if (invalid.length > 0) {
        toast.warning(translations[langMode]?.orgInvalidEmails ?? "Please enter valid email addresses.");
        return;
      }
    }
    if (isLogoUploading) {
      toast.info(translations[langMode].uploading || "Image is still uploading. Please wait.");
      return;
    }
    setIsLoading(true);
    try {
      const data = await createOrganization(selectedOrgName, logoUrl, emails);
      if (data.header.code === 600) {
        toast.success(translations[langMode].orgaddedsuc);
        setSelectedOrgName("");
        setLogoUrl("");
        setLogoPreview(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
        setReportEmails([""]);
        setOpen(false);
        fetchorgnizationlist();
      } else {
        toast.error(data.body.value);
      }
    } catch (error) {
      toast.error(error?.response?.data?.body?.value || translations[langMode].errorcreatingorg);
    } finally {
      setIsLoading(false); // End loading
    }
  };

  const handleClick = () => {
    if (isLogoUploading) return;
    fileInputRef.current?.click();
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={setOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div
            className="fixed inset-0 z-40 bg-gray-500 bg-opacity-75 transition-opacity"
          />
        </Transition.Child>
        <div className="fixed inset-0 z-50 w-screen overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-2 text-center sm:p-4">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel
                dir={isRtl ? "rtl" : "ltr"}
                className={`relative flex max-h-[calc(100dvh-1rem)] w-full transform flex-col overflow-hidden rounded-xl bg-white shadow-xl transition-all sm:my-8 sm:max-w-lg sm:rounded-2xl ${isRtl ? "text-right" : "text-left"} ${theme === "dark"
                  ? "dark-mode"
                  : theme === "high-contrast"
                    ? "high-contrast"
                    : ""
                  }`}
              >
                <div className={`bg-white ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""}`}>
                  <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-600">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {translations[langMode].addOrganization}
                    </h3>
                    <ModalCloseButton
                      onClick={() => setOpen(false)}
                      ariaLabel="Close modal"
                    />
                  </div>
                  <div className="min-h-0 flex-1 space-y-6 overflow-y-auto px-3 py-4 sm:px-5 sm:py-6">
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 dark:text-white mb-1 ${isRtl ? "text-right" : ""}`}>
                        {translations[langMode].orgname}
                      </label>
                      <input
                        type="text"
                        dir={isRtl ? "rtl" : "ltr"}
                        value={selectedOrgName}
                        onChange={handleOrgNameChange}
                        placeholder={translations[langMode].entername}
                        className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${isRtl ? "text-right" : "text-left"} ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : ""
                          }`}
                      />
                    </div>
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 dark:text-white mb-2 ${isRtl ? "text-right" : ""}`}>
                        {translations[langMode].OrganizationLogo}
                      </label>
                      <div className="relative">
                      {!logoPreview ? (
                        <div
                          onClick={handleClick}
                          className={`flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-blue-500 rounded-lg transition ${isLogoUploading ? "cursor-wait opacity-70" : "cursor-pointer hover:bg-blue-50"}`}
                        >
                          <img
                            src="https://res.cloudinary.com/dpdvpazfv/image/upload/v1753344927/uploadFile_qyzcdu.png"
                            alt="Upload Icon"
                            className="w-8 h-8 mb-2"
                          />
                          <p className="text-sm text-gray-700 font-medium">
                            {translations[langMode].uploadOrganizationLogo}
                          </p>
                        </div>
                      ) : (
                        <div className="flex items-center gap-4 min-h-[6rem]">
                          <img
                            src={logoPreview}
                            alt="Logo preview"
                            className={`w-24 h-24 object-cover rounded-full border-2 border-gray-300 ${isLogoUploading ? "opacity-50" : ""}`}
                          />
                          <button data-button-intent="primary"
                            type="button"
                            onClick={handleClick}
                            disabled={isLogoUploading}
                            className="px-3 py-1 text-sm font-medium text-blue-600 border border-blue-500 rounded hover:bg-blue-50 disabled:opacity-50 disabled:cursor-not-allowed"
                          >
                            {translations[langMode].uploadOrganizationLogo}
                          </button>
                        </div>
                      )}
                      <input
                        type="file"
                        accept="image/*"
                        className="hidden"
                        ref={fileInputRef}
                        onChange={handleLogoChange}
                        disabled={isLogoUploading}
                      />
                      {isLogoUploading && (
                        <div className="absolute inset-0 flex flex-col items-center justify-center rounded-lg bg-white/80 dark:bg-gray-900/80 z-10">
                          <TbLoader className="animate-spin text-blue-600 dark:text-blue-400" size={28} />
                          <p className="mt-2 text-sm font-medium text-gray-700 dark:text-gray-200">
                            {translations[langMode].uploading}
                          </p>
                        </div>
                      )}
                      </div>
                    </div>
                    <div>
                      <label className={`block text-sm font-medium text-gray-700 dark:text-white mb-1 ${isRtl ? "text-right" : ""}`}>
                        {translations[langMode].reportEmailsOptional}
                      </label>
                      <p className={`text-xs text-gray-500 dark:text-gray-400 mb-2 ${isRtl ? "text-right" : ""}`}>
                        {translations[langMode].reportEmailsDescription}
                      </p>
                      <div className="space-y-2">
                        {reportEmails.map((email, index) => (
                          <div key={index} className="flex gap-2 items-center">
                            <input
                              type="email"
                              dir="ltr"
                              value={email}
                              onChange={(e) => setReportEmailAt(index, e.target.value)}
                              placeholder="email@example.com"
                              className={`flex-1 rounded-md border px-3 py-2 text-sm text-left shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 ${theme === "dark" ? "dark-mode" : theme === "high-contrast" ? "high-contrast" : "border-gray-300"}`}
                            />
                            <button
                              type="button"
                              onClick={() => removeReportEmail(index)}
                              disabled={reportEmails.length <= 1}
                              className="p-2 rounded text-[#FF0000] hover:bg-red-50 disabled:opacity-40 disabled:cursor-not-allowed dark:hover:bg-gray-600"
                              title={translations[langMode].removeEmail}
                            >
                              <Trash2 size={18} />
                            </button>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={addReportEmail}
                          className={`text-sm font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400 ${isRtl ? "text-right w-full" : ""}`}
                        >
                          + {translations[langMode].addAnotherEmail}
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="shrink-0 sticky bottom-0 bg-white dark:bg-gray-800 flex w-full justify-end items-center gap-2 sm:gap-3 border-t px-4 sm:px-5 py-3 sm:py-4">
                    <CommonButton colorIntent="danger"
                      onClick={() => setOpen(false)}
                      variant="danger"
                      wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                      className="w-full !h-10 !rounded-lg justify-center"
                    >
                      {translations[langMode].cancel}
                    </CommonButton>
                    <CommonButton colorIntent="success"
                      onClick={handleAddOrganization}
                      variant="primary"
                      disabled={isLoading || isLogoUploading}
                      wrapperClassName="flex-1 min-w-0 flex sm:inline-block sm:flex-initial"
                      className="w-full !h-10 !rounded-lg justify-center"
                    >
                      {isLoading ? translations[langMode].Saving : translations[langMode].save}
                    </CommonButton>
                  </div>
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}
